/**
 * Which CSV a download button writes, chosen from the computer's REGIONAL number format
 * (`docs/adr/9072`, addendum of 2026-10-23, owner ruling).
 *
 * Excel reads a CSV with the system's list and decimal separators, so a person whose computer writes
 * `1,5` needs the `;`-separated, decimal-comma file and anyone else needs the raw one. That is a fact about
 * the computer, NOT about the app's language: an Italian planner on an English-formatted PC, or an
 * English-speaking one on an Italian-formatted PC, is served by the computer. So the app's i18n language
 * is deliberately not consulted here — `new Intl.NumberFormat()` with no locale is the browser's own default.
 *
 * The raw CSV (G2.4) is untouched either way; this only decides which of the two files the single «CSV»
 * button gives, and which one the small «altro formato CSV» link beside it gives.
 */

import type { CsvDialect } from './types';

/** The two CSV formats a button can ask `runExport` for. */
export type CsvFormat = 'csv' | 'csv-decimal-comma';

/**
 * The decimal separator the computer writes numbers with: what `Intl.NumberFormat` (no locale: the browser's default,
 * not the app's language) puts between 1 and 5 in 1.5. `locales` is for tests; `undefined` is the browser's.
 */
export function decimalSeparator(locales?: string | readonly string[]): string {
  try {
    const parts = new Intl.NumberFormat(locales === undefined ? undefined : [...(typeof locales === 'string' ? [locales] : locales)]).formatToParts(1.5);
    return parts.find((part) => part.type === 'decimal')?.value ?? '.';
  } catch {
    // A runtime without Intl, or a locale it rejects: the raw file, which is the one every system reads.
    return '.';
  }
}

/** The CSV dialect that suits a decimal separator: a comma gives the comma-decimal file, anything else the raw one. */
export function csvDialectForDecimal(decimal: string): CsvDialect {
  return decimal === ',' ? 'decimal-comma' : 'standard';
}

/** The format of the dialect: `csv-decimal-comma` or `csv`. */
export function csvFormatOf(dialect: CsvDialect): CsvFormat {
  return dialect === 'decimal-comma' ? 'csv-decimal-comma' : 'csv';
}

/** The CSV the computer's regional number format wants: the one the «CSV» button writes. Pure but for the browser's default locale. */
export function regionalCsvFormat(locales?: string | readonly string[]): CsvFormat {
  return csvFormatOf(csvDialectForDecimal(decimalSeparator(locales)));
}

/** The other CSV: what the «altro formato CSV» link writes. */
export function otherCsvFormat(format: CsvFormat): CsvFormat {
  return format === 'csv' ? 'csv-decimal-comma' : 'csv';
}

/** One button of a download row. `format` is what it writes; `words` is the declared format whose label and busy words it wears. */
export interface DownloadButton<F extends string> {
  readonly format: F | CsvFormat;
  readonly words: F;
  /** Excel is the primary action of the row; the CSV and the rest are secondary. */
  readonly primary: boolean;
}

/** A download row: its buttons, in order, and the other CSV for the small link beside the CSV button (`null` when there is no CSV). */
export interface DownloadArrangement<F extends string> {
  readonly buttons: readonly DownloadButton<F>[];
  readonly other: CsvFormat | null;
}

/**
 * What every export's download row offers (owner ruling 2026-10-23): «Excel» first and primary; ONE «CSV» button, in the dialect
 * the computer's regional format wants (`preferred`); and the other dialect as a small link. `formats` are the formats the screen
 * declares — `csv` and `csv-decimal-comma` both stand for the CSV, and the other formats (png, svg…) keep their places.
 */
export function arrangeDownloads<F extends string>(
  formats: readonly F[], preferred: CsvFormat = regionalCsvFormat(),
): DownloadArrangement<F> {
  // The CSV button wears the screen's plain CSV words — «Scarica CSV (120 righe)» — whichever dialect it writes.
  const csvWords = (formats.includes('csv' as F) ? 'csv' : 'csv-decimal-comma') as F;
  const buttons: DownloadButton<F>[] = [];
  let csvPlaced = false;
  for (const format of formats) {
    if (format === 'csv' || format === 'csv-decimal-comma') {
      if (csvPlaced) continue;
      csvPlaced = true;
      buttons.push({ format: preferred, words: csvWords, primary: false });
      continue;
    }
    buttons.push({ format, words: format, primary: format === 'xlsx' });
  }
  const excel = buttons.findIndex((button) => button.primary);
  if (excel > 0) buttons.unshift(...buttons.splice(excel, 1));
  return { buttons, other: csvPlaced ? otherCsvFormat(preferred) : null };
}
